import { EditorSection } from '@/components/plot/editor/EditorSection';
import { FigurePanel, useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MoveHorizontal, MoveVertical } from 'lucide-react';

interface PanelLabelTabProps {
  panel: FigurePanel;
}

export function PanelLabelTab({ panel }: PanelLabelTabProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const updatePanelOverrides = useFigureCompositionStore((state) => state.updatePanelOverrides);
  
  // Check if legend is visible for this panel
  const legendPosition = currentComposition?.legendPosition ?? 'auto';
  const isLegendVisible = legendPosition !== 'none';

  return (
    <div className="space-y-4">
      <EditorSection
        id="panel-label"
        title="Panel label"
        description='Label panels for references like "A", "B", "C".'
      >
        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Label</Label>
            <Input
              type="text"
              className="h-8 text-sm"
              value={panel.label}
              onChange={(e) => updatePanel(panel.id, { label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">Position</Label>
            <Select
              value={panel.labelStyle?.position ?? 'top-left'}
              onValueChange={(value) => {
                updatePanel(panel.id, {
                  labelStyle: {
                    ...panel.labelStyle,
                    position: value as any,
                  },
                });
              }}
            >
              <SelectTrigger className="mt-1 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top-left">Top-left</SelectItem>
                <SelectItem value="top-right">Top-right</SelectItem>
                <SelectItem value="bottom-left">Bottom-left</SelectItem>
                <SelectItem value="bottom-right">Bottom-right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="label-bold"
                checked={panel.labelStyle?.fontWeight === 'bold'}
                onCheckedChange={(checked) => {
                  updatePanel(panel.id, {
                    labelStyle: {
                      ...panel.labelStyle,
                      fontWeight: checked === true ? 'bold' : 'normal',
                    },
                  });
                }}
              />
              <Label htmlFor="label-bold" className="text-xs cursor-pointer">
                Bold
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="label-italic"
                checked={panel.labelStyle?.fontStyle === 'italic'}
                onCheckedChange={(checked) => {
                  updatePanel(panel.id, {
                    labelStyle: {
                      ...panel.labelStyle,
                      fontStyle: checked === true ? 'italic' : 'normal',
                    },
                  });
                }}
              />
              <Label htmlFor="label-italic" className="text-xs cursor-pointer">
                Italic
              </Label>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Font size (pt)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                className="w-20 h-8 text-sm"
                value={panel.labelStyle?.fontSize ?? 10}
                onChange={(e) => {
                  const fontSize = parseInt(e.target.value, 10);
                  if (!isNaN(fontSize) && fontSize > 0) {
                    updatePanel(panel.id, {
                      labelStyle: {
                        ...panel.labelStyle,
                        fontSize,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </EditorSection>

      <EditorSection
        id="panel-legend"
        title="Panel legend"
        description="Override legend visibility and fine-tune position for this panel."
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-legend"
                checked={panel.layoutOverrides?.showLegend ?? true}
                onCheckedChange={(checked) =>
                  updatePanelOverrides(panel.id, { showLegend: checked === true })
                }
              />
              <Label htmlFor="show-legend" className="text-sm cursor-pointer">
                Show legend
              </Label>
            </div>
            {panel.layoutOverrides?.showLegend !== false && (
              <div className="space-y-1 pl-6">
                <Label className="text-sm text-muted-foreground">Legend position</Label>
                <Select
                  value={panel.layoutOverrides?.legendPosition || 'top-right'}
                  onValueChange={(value) =>
                    updatePanelOverrides(panel.id, {
                      legendPosition: value as any,
                    })
                  }
                >
                  <SelectTrigger className="mt-1 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-right">Top-right</SelectItem>
                    <SelectItem value="top-left">Top-left</SelectItem>
                    <SelectItem value="bottom-right">Bottom-right</SelectItem>
                    <SelectItem value="bottom-left">Bottom-left</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Legend fine-tune controls */}
          <div className={`pt-3 border-t space-y-3 ${!isLegendVisible ? 'opacity-40 pointer-events-none' : ''}`}>
            {!isLegendVisible && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded mb-2">
                Legend is hidden. Enable legend in figure settings to use fine-tune controls.
              </div>
            )}
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MoveHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Horizontal fine-tune</Label>
                  <span className="text-xs ml-auto text-muted-foreground">
                    {(panel.layoutOverrides?.legendOffsetX ?? 0).toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[panel.layoutOverrides?.legendOffsetX ?? 0]}
                  min={-0.5}
                  max={0.5}
                  step={0.02}
                  onValueChange={(value) => {
                    const clampedValue = Math.max(-0.5, Math.min(0.5, value[0]));
                    updatePanelOverrides(panel.id, { legendOffsetX: clampedValue });
                  }}
                  disabled={!isLegendVisible}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>Left</span>
                  <span>Center</span>
                  <span>Right</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MoveVertical className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Vertical fine-tune</Label>
                  <span className="text-xs ml-auto text-muted-foreground">
                    {(panel.layoutOverrides?.legendOffsetY ?? 0).toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[panel.layoutOverrides?.legendOffsetY ?? 0]}
                  min={-0.5}
                  max={0.5}
                  step={0.02}
                  onValueChange={(value) => {
                    const clampedValue = Math.max(-0.5, Math.min(0.5, value[0]));
                    updatePanelOverrides(panel.id, { legendOffsetY: clampedValue });
                  }}
                  disabled={!isLegendVisible}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>Down</span>
                  <span>Center</span>
                  <span>Up</span>
                </div>
              </div>
            </div>
            {isLegendVisible && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs w-full"
                  onClick={() => {
                    updatePanelOverrides(panel.id, { legendOffsetX: 0, legendOffsetY: 0 });
                  }}
                  disabled={!isLegendVisible}
                >
                  Reset offsets
                </Button>
              </div>
            )}
          </div>
        </div>
      </EditorSection>

      {panel.plotId && (
        <EditorSection
          id="plot-titles"
          title="Plot titles"
          description="Hide plot title or subtitle in this panel."
        >
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-title"
                checked={panel.layoutOverrides?.hideTitle ?? false}
                onCheckedChange={(checked) =>
                  updatePanelOverrides(panel.id, { hideTitle: checked === true })
                }
              />
              <Label htmlFor="hide-title" className="text-sm cursor-pointer">
                Hide title
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-subtitle"
                checked={panel.layoutOverrides?.hideSubtitle ?? false}
                onCheckedChange={(checked) =>
                  updatePanelOverrides(panel.id, { hideSubtitle: checked === true })
                }
              />
              <Label htmlFor="hide-subtitle" className="text-sm cursor-pointer">
                Hide subtitle
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-annotations"
                checked={panel.layoutOverrides?.hideAnnotations ?? false}
                onCheckedChange={(checked) =>
                  updatePanelOverrides(panel.id, { hideAnnotations: checked === true })
                }
              />
              <Label htmlFor="hide-annotations" className="text-sm cursor-pointer">
                Hide annotations
              </Label>
            </div>
          </div>
        </EditorSection>
      )}
    </div>
  );
}

